import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import {
  ClientDocumentsActions,
  ClientDocumentsEffectsActions,
} from '@lockton-apps/clients/actions';
import { DashboardContactActions } from '@lockton-apps/dashboard/action';
import { PolicyDetailsActions } from '@lockton-apps/policies/action';
import { DocumentsApiActions } from '@lockton-apps/shared/action/documents';
import {
  Document,
  DocumentCreate,
  DocumentTag,
} from '@lockton-apps/shared/interfaces';

export const DOCUMENTS_FEATURE_KEY = 'documents';

export interface DocumentsState extends EntityState<Document> {
  selectedId?: number | null; // which Documents record has been selected
  loaded: boolean; // has the Documents list been loaded
  mutationsLoading: boolean;
  error?: string | null; // last known error (if any)
  uploadSuccess: Document[];
  uploadFailure: DocumentCreate[];
  documentInUpdate: Document | null;
  tags: DocumentTag[];
  tagsLoaded: boolean;
}

export interface DocumentsPartialState {
  readonly [DOCUMENTS_FEATURE_KEY]: DocumentsState;
}

export const documentsAdapter: EntityAdapter<Document> =
  createEntityAdapter<Document>();

export const initialDocumentsState: DocumentsState =
  documentsAdapter.getInitialState({
    loaded: false,
    mutationsLoading: false,
    uploadSuccess: [],
    uploadFailure: [],
    documentInUpdate: null,
    filters: {
      sortBy: { order: 'asc', path: 'created' },
      category: '',
      tags: '',
      visibility: '',
      startDate: null,
      endDate: null,
    },
    tags: [],
    tagsLoaded: false,
  });

const reducer = createReducer(
  initialDocumentsState,

  on(PolicyDetailsActions.init, (state) => ({
    ...state,
    tagsLoaded: false,
    error: null,
  })),
  on(DocumentsApiActions.loadTagsSuccess, (state, { tags }) => ({
    ...state,
    tags,
    tagsLoaded: true,
  })),
  on(DocumentsApiActions.loadTagsFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  on(ClientDocumentsActions.documentMoved, (state, { id }) =>
    documentsAdapter.removeOne(id, {
      ...state,
      documentInUpdate: state.entities[id] as Document,
    })
  ),
  on(
    ClientDocumentsActions.documentUpdated,
    PolicyDetailsActions.documentUpdated,
    (state, { document }) =>
      documentsAdapter.updateOne(
        { id: document.id as number, changes: document },
        {
          ...state,
          documentInUpdate: state.entities[document.id as number] as Document,
        }
      )
  ),
  on(
    ClientDocumentsActions.documentDeleted,
    DashboardContactActions.documentDeleted,
    PolicyDetailsActions.documentDeleted,
    (state, { id }) =>
      documentsAdapter.removeOne(id, {
        ...state,
        documentInUpdate: state.entities[id] as Document,
      })
  ),
  on(DocumentsApiActions.deletedFailure, (state) =>
    documentsAdapter.setOne(state.documentInUpdate as Document, state)
  ),
  // Client Documents
  on(
    ClientDocumentsActions.init,
    ClientDocumentsEffectsActions.navigated,
    DashboardContactActions.folderSelected,
    PolicyDetailsActions.init,

    (state) =>
      documentsAdapter.removeAll({
        ...state,
        loaded: false,
        error: null,
      })
  ),
  on(
    DocumentsApiActions.folderDocumentsLoadedSuccess,
    DocumentsApiActions.loadPolicyDocumentsSuccess,
    (state, { documents }) =>
      documentsAdapter.setAll(documents, { ...state, loaded: true })
  ),
  on(DocumentsApiActions.folderDocumentsLoadedFailure, (state, { error }) => ({
    ...state,
    loaded: true,
    error,
  })),

  on(DocumentsApiActions.documentsUploadCompleted, (state, { success }) =>
    documentsAdapter.addMany(success, {
      ...state,
      uploadSuccess: [],
      uploadFailure: [],
    })
  ),

  on(
    DashboardContactActions.destroyed,
    PolicyDetailsActions.destroyed,
    (state) =>
      documentsAdapter.removeAll({
        ...state,
        selectedId: null,
      })
  )
);

export function documentsReducer(
  state: DocumentsState | undefined,
  action: Action
) {
  return reducer(state, action);
}
